import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
} from "@aksara-ui/react";
import { InputCheckbox } from "components/Form/InputCheckbox";
import { Field, FieldProps, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCatalogCategory,
  getAllCatalogCategories,
  storeCatalogCategory,
  updateCatalogCategory,
} from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  onCategoryCreated?: () => void;
  currentCategory?: any;
  mode?: "create" | "edit";
}

const CatalogCategoryFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  onCategoryCreated,
  currentCategory,
  mode = "create",
}) => {
  const [formMode, setFormMode] = useState<"create" | "edit">(mode);
  const [selectedCategory, setSelectedCategory] = useState<any>(currentCategory ?? null);
  const dispatch = useDispatch();
  const { isFormLoading, catalogCategories } = useSelector(
    (state: any) => state.catalogReducer
  );

  useEffect(() => {
    setFormMode(mode);
    setSelectedCategory(currentCategory ?? null);
  }, [mode, currentCategory, isOpen]);

  const categoryList = (Array.isArray(catalogCategories)
    ? catalogCategories
    : []
  ).filter(
    (category: any) =>
      category && category.category !== "all" && category.name !== "all"
  );

  const handleCloseModal = () => {
    onClose && onClose();
    setFormMode("create");
    setSelectedCategory(null);
    if (onCategoryCreated) {
      onCategoryCreated();
    }
  };

  const handleDeleteCategory = (category: any) => {
    if (!category?.id) return;
    dispatch(
      deleteCatalogCategory(String(category.id), () => {
        dispatch(getAllCatalogCategories() as any);
        if (selectedCategory?.id === category.id) {
          setSelectedCategory(null);
          setFormMode("create");
        }
      }) as any
    );
  };

  const handleSubmit = (values: any) => {
    const { categoryName, isAllUser } = values;

    if (formMode === "edit" && selectedCategory?.id) {
      dispatch(
        updateCatalogCategory(
          String(selectedCategory.id),
          {
            categoryName,
            isAllUser,
          },
          handleCloseModal
        ) as any
      );
      return;
    }

    dispatch(
      storeCatalogCategory(
        {
          categoryName,
          isAllUser,
        },
        handleCloseModal
      ) as any
    );
  };

  const validationSchema = yup.object({
    categoryName: yup.string().required("Category Name is required field"),
  });

  const initialValues = {
    categoryName: selectedCategory?.name || "",
    isAllUser:
      selectedCategory !== undefined && selectedCategory !== null
        ? Boolean(
            selectedCategory.is_all_user ?? selectedCategory.isAllUser ?? true
          )
        : true,
  };

  return (
    <Dialog disableOverlayClick isOpen={isOpen} onClose={() => handleCloseModal()}>
      <Formik
        key={formMode === "edit" ? `edit-${selectedCategory?.id ?? "new"}` : "create"}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <DialogHeader>
              <Text fontWeight={700}>
                {formMode === "edit" ? "Edit Catalog Category" : "Add Catalog Category"}
              </Text>
            </DialogHeader>
            <DialogContent>
              <FormWrapper>
                <Field name="categoryName">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="categoryName">Category Name</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                <Stack spacing="xs">
                  <FormLabel htmlFor="isAllUser">Visibility</FormLabel>
                  <Stack display="flex" flexDirection="row" alignItems="center" style={{ gap: 10 }}>
                    <InputCheckbox
                      id="isAllUser"
                      checked={values.isAllUser}
                      onCheckedChange={(checked: any) => {
                        const nextValue =
                          checked === true ||
                          checked === "true" ||
                          checked === 1 ||
                          checked === "1";
                        setFieldValue("isAllUser", nextValue);
                      }}
                    />
                    <Text>{values.isAllUser ? "For all users" : "Only for specific users"}</Text>
                  </Stack>
                </Stack>

                <Stack spacing="xs">
                  <Text fontWeight={700}>Category List</Text>
                  {categoryList.length === 0 ? (
                    <Text color="grey06">No category yet</Text>
                  ) : (
                    <Table>
                      <TableHead>
                        <TableHeadRow>
                          <TableHeadCell>Name</TableHeadCell>
                          <TableHeadCell>Visibility</TableHeadCell>
                          <TableHeadCell>Action</TableHeadCell>
                        </TableHeadRow>
                      </TableHead>
                      <TableBody>
                        {categoryList.map((category: any) => (
                          <TableBodyRow key={category.id ?? category.name}>
                            <TableBodyCell>
                              {category.name || category.category}
                            </TableBodyCell>
                            <TableBodyCell>
                              {category.is_all_user || category.isAllUser ? "For all users" : "Specific users"}
                            </TableBodyCell>
                            <TableBodyCell>
                              <Stack display="flex" flexDirection="row" style={{ gap: 8 }}>
                                <Button
                                  variant="outline"
                                  size={32}
                                  onClick={() => {
                                    setSelectedCategory(category);
                                    setFormMode("edit");
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="destructive"
                                  size={32}
                                  onClick={() => handleDeleteCategory(category)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </TableBodyCell>
                          </TableBodyRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Stack>
              </FormWrapper>
            </DialogContent>
            <DialogFooter>
              <Stack
                display="flex"
                flexDirection="row"
                spacing="xs"
                flex={1}
                style={{ gap: 15 }}
              >
                <Button
                  block
                  variant="outline"
                  onClick={() => {
                    setSelectedCategory(null);
                    setFormMode("create");
                    handleCloseModal();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={isFormLoading} block variant="primary">
                  {formMode === "edit" ? "Save" : "Add"}
                </Button>
              </Stack>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CatalogCategoryFormModal;
